/* ============================================================
   Ogé Exclusive, reels rail controller

   Three jobs, all of them about the fact that a reels rail has one
   focal point:

   1. Move `is-active`/`is-near` to whichever card is nearest the
      middle of the viewport, so the handset frame follows the scroll
      rather than being pinned to a block index.
   2. Play only that card's clip, and only while the section is on
      screen. Five portrait videos autoplaying at once costs more than
      the rest of the page; `preload` starts at "none" in the markup
      and is raised the first time a card becomes the active one.
   3. Drive the arrows, and make the first click on an off-centre card
      centre it instead of following its link.

   The rail is a plain overflow-scroll container with CSS scroll snap,
   so with this file missing it still scrolls, snaps and reads fine, the markup seeds the frame on the first card for exactly that case.
   ============================================================ */
(function () {
  'use strict';

  var READY = 'ogeReelsReady';

  function prefersReducedMotion() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function setup(root) {
    if (root.dataset[READY] === '1') return;

    var viewport = root.querySelector('[data-oge-reels-viewport]');
    var reels = Array.prototype.slice.call(root.querySelectorAll('[data-oge-reel]'));
    if (!viewport || !reels.length) return;

    root.dataset[READY] = '1';

    var prev = root.querySelector('[data-oge-reels-prev]');
    var next = root.querySelector('[data-oge-reels-next]');
    var active = -1;
    var onScreen = true;
    var ticking = false;

    /* ------------------------------------------------------------
       Endless rail.

       The track is tripled, one set before, one after, and the
       scroll is silently returned to the middle set whenever it
       crosses into a copy. Because every set is identical and exactly
       one set wide, the jump lands on the same pixel of the same card
       and cannot be seen. Cloning is cheap here: the clips carry
       `preload="none"`, so a copy costs a <video> element and nothing
       on the wire.

       Only worth doing when the track actually overflows. With five
       cards on a wide screen there is nothing to scroll, and cloning
       would just triple the DOM to hold a rail that never moves.
       ------------------------------------------------------------ */
    var track = viewport.firstElementChild;
    var setSize = reels.length;
    var setWidth = 0;
    var looping = false;

    function measure() {
      if (!looping) return;
      var first = reels[0];
      var firstOfSecond = reels[setSize];
      setWidth = firstOfSecond ? firstOfSecond.offsetLeft - first.offsetLeft : 0;
    }

    if (track && setSize > 1 && viewport.scrollWidth > viewport.clientWidth + 8) {
      var originals = reels.slice();
      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();

      originals.forEach(function (reel) {
        before.appendChild(reel.cloneNode(true));
        after.appendChild(reel.cloneNode(true));
      });

      track.insertBefore(before, track.firstChild);
      track.appendChild(after);

      reels = Array.prototype.slice.call(root.querySelectorAll('[data-oge-reel]'));
      looping = true;
      measure();
    }

    /* Assigning scrollLeft would animate: `scroll-behavior: smooth`
       lives in CSS. The jump has to be instant or the seam shows. */
    function jump(delta) {
      var previous = viewport.style.scrollBehavior;
      viewport.style.scrollBehavior = 'auto';
      viewport.scrollLeft += delta;
      window.requestAnimationFrame(function () {
        viewport.style.scrollBehavior = previous;
      });
    }

    function wrap() {
      if (!looping || !setWidth) return false;
      if (viewport.scrollLeft < setWidth * 0.5) {
        jump(setWidth);
        return true;
      }
      if (viewport.scrollLeft > setWidth * 1.5) {
        jump(-setWidth);
        return true;
      }
      return false;
    }

    function centerOf(reel) {
      return reel.offsetLeft + reel.offsetWidth / 2;
    }

    function playable(reel) {
      return reel.querySelector('[data-oge-reel-video]');
    }

    function syncPlayback() {
      reels.forEach(function (reel, i) {
        var video = playable(reel);
        if (!video) return;

        if (i === active && onScreen && !document.hidden) {
          if (video.preload === 'none') video.preload = 'auto';
          // Safari kijkt naar de eigenschappen, niet naar de attributen,
          // en de rail kloont zijn kaarten voor de eindeloze lus. Zonder
          // dit weigert play() op iOS met NotAllowedError.
          video.muted = true;
          video.playsInline = true;
          var started = video.play();
          // Autoplay can still be refused (data saver, low power mode).
          // The poster stays up, which is a fine resting state.
          if (started && started.catch) started.catch(function () {});
        } else {
          video.pause();
        }
      });
    }

    function apply(index) {
      if (index === active) return;
      active = index;

      reels.forEach(function (reel, i) {
        var distance = Math.abs(i - index);
        reel.classList.toggle('is-active', distance === 0);
        reel.classList.toggle('is-near', distance === 1);
      });

      if (prev) prev.disabled = !looping && index <= 0;
      if (next) next.disabled = !looping && index >= reels.length - 1;

      syncPlayback();
    }

    function nearest() {
      var middle = viewport.scrollLeft + viewport.clientWidth / 2;
      var best = 0;
      var bestDistance = Infinity;

      reels.forEach(function (reel, i) {
        var distance = Math.abs(centerOf(reel) - middle);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });

      return best;
    }

    function center(index, instant) {
      if (looping) {
        // Past either end there is always another copy to travel into.
        index = ((index % reels.length) + reels.length) % reels.length;
      } else {
        index = Math.max(0, Math.min(reels.length - 1, index));
      }
      var left = centerOf(reels[index]) - viewport.clientWidth / 2;

      if (instant || prefersReducedMotion() || !viewport.scrollTo) {
        // `scroll-behavior: smooth` lives in CSS, so an instant jump has
        // to opt out explicitly rather than just assigning scrollLeft.
        if (viewport.scrollTo) {
          viewport.scrollTo({ left: left, behavior: 'auto' });
        } else {
          viewport.scrollLeft = left;
        }
      } else {
        viewport.scrollTo({ left: left, behavior: 'smooth' });
      }

      apply(index);
    }

    viewport.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          wrap();
          apply(nearest());
        });
      },
      { passive: true }
    );

    if (prev) {
      prev.addEventListener('click', function () {
        center(active - 1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        center(active + 1);
      });
    }

    reels.forEach(function (reel, i) {
      reel.addEventListener('click', function (event) {
        if (i === active) return;
        // An off-centre card is half-hidden behind its neighbours;
        // treat the first click as "bring this one forward".
        event.preventDefault();
        center(i);
      });
    });

    viewport.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        center(active + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        center(active - 1);
      }
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        measure();
        center(active, true);
      }, 150);
    });

    document.addEventListener('visibilitychange', syncPlayback);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          onScreen = entries[entries.length - 1].isIntersecting;
          syncPlayback();
        },
        { threshold: 0.2 }
      ).observe(root);
    }

    // Open on the middle clip, the way the design reads. With the rail
    // tripled that means the middle card of the *middle* set, which
    // leaves a full copy of runway in both directions. Instant, so it
    // lands before the section's reveal transition finishes.
    var opening = looping
      ? setSize + Math.floor((setSize - 1) / 2)
      : Math.floor((reels.length - 1) / 2);
    center(opening, true);
  }

  /* ------------------------------------------------------------
     Het blokkenraster.

     Geen middelpunt, dus geen keuze te maken: elke tegel speelt zodra
     hij in beeld is en staat stil zodra hij eruit is. Het autoplay-
     attribuut staat al in de opmaak, want een muted inline video mag
     van elke browser uit zichzelf starten en dat moet niet van dit
     bestand afhangen. Wat hier gebeurt is het omgekeerde: pauzeren wat
     niemand ziet, zodat een raster van vijf clips niet de hele tijd
     vijf decoders bezet houdt.

     `prefers-reduced-motion` zet deze clips *niet* stil. Ze zijn de
     inhoud van de sectie, geen versiering: een raster van stilstaande
     posters is geen rustiger versie van dit blok, het is een leeg blok.
     Die voorkeur gaat over beweging die de pagina zelf maakt, en die
     wordt hier wel gehoorzaamd: de rail springt dan zonder smooth
     scroll naar zijn plek. Zie `center()`.
     ------------------------------------------------------------ */
  function setupGrid(root) {
    if (root.dataset[READY] === '1') return;

    var videos = Array.prototype.slice.call(
      root.querySelectorAll('[data-oge-reel-video]')
    );
    if (!videos.length) return;

    root.dataset[READY] = '1';

    function play(video) {
      if (document.hidden) return;
      // Op iOS is het attribuut alleen niet altijd genoeg: Safari kijkt
      // naar de eigenschappen, en die staan los van de opmaak zodra een
      // element door script is aangeraakt of gekloond. Zonder deze twee
      // regels weigert play() daar met NotAllowedError, precies op de
      // telefoons waar het moet werken.
      video.muted = true;
      video.playsInline = true;
      if (video.preload === 'none') video.preload = 'auto';
      var started = video.play();
      // Autoplay kan alsnog geweigerd worden (databesparing, low power
      // mode). De poster blijft staan, en dat is een goede ruststand.
      if (started && started.catch) started.catch(function () {});
    }

    if (!('IntersectionObserver' in window)) {
      videos.forEach(play);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            video.dataset.ogeOnScreen = '1';
            play(video);
          } else {
            delete video.dataset.ogeOnScreen;
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    videos.forEach(function (video) {
      observer.observe(video);
    });

    document.addEventListener('visibilitychange', function () {
      videos.forEach(function (video) {
        if (video.dataset.ogeOnScreen !== '1') return;
        if (document.hidden) {
          video.pause();
        } else {
          play(video);
        }
      });
    });
  }

  function init(scope) {
    var context = scope || document;
    context.querySelectorAll('[data-oge-reels]').forEach(setup);
    context.querySelectorAll('[data-oge-reelgrid]').forEach(setupGrid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: sections are replaced wholesale, so the fresh copy
  // arrives without the ready flag and needs wiring up again.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var reel = event.target.closest ? event.target.closest('[data-oge-reel]') : null;
    var root = reel && reel.closest('[data-oge-reels]');
    if (!root) return;
    var reels = Array.prototype.slice.call(root.querySelectorAll('[data-oge-reel]'));
    var index = reels.indexOf(reel);
    if (index < 0) return;
    var viewport = root.querySelector('[data-oge-reels-viewport]');
    if (!viewport) return;
    viewport.scrollTo({
      left: reel.offsetLeft + reel.offsetWidth / 2 - viewport.clientWidth / 2,
      behavior: 'smooth',
    });
  });
})();
